/**
 * Banners API (slider). Used for main screen background and other banner imagery.
 */
import { apiGet } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';

export interface Banner {
  id?: number;
  slug?: string;
  attachment?: string;
  image?: string;
  /** External link (YouTube / direct media). Prefer over `attachment` when non-empty. */
  url?: string;
  title?: string;
  arabic_title?: string;
  description?: string;
  arabic_description?: string;
  is_active?: boolean;
  [key: string]: unknown;
}

export type BannerMediaKind = 'image' | 'video' | 'youtube';

/** Resolved hero media for a banner: `url` wins over `attachment`, with attachment as fallback. */
export type BannerHeroMedia = {
  kind: BannerMediaKind;
  uri: string;
  youtube_id: string | null;
  fallback_kind: BannerMediaKind | null;
  fallback_uri: string | null;
};

/**
 * React Query key for the banner list. Defined next to `fetchBanners` so `authSessionApi` / `App.tsx`
 * can import it without pulling banner hooks → API barrel → `channel` → `authSessionApi` (require cycle).
 */
export const bannersQueryKey = ['banners'] as const;

/** Common CMS/API keys for slider ordering (first = main hero). Checked in order. */
const BANNER_ORDER_FIELD_KEYS = [
  'order',
  'sort_order',
  'position',
  'priority',
  'sequence',
  'rank',
  'sort',
] as const;

const VIDEO_EXT = /\.(mp4|mov|m4v|webm|mkv|m3u8)(\?|#|$)/i;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|heic|avif)(\?|#|$)/i;

function numericFromUnknown(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = parseFloat(v);
    if (Number.isFinite(n)) {
      return n;
    }
  }
  return null;
}

function nonEmptyUrl(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Stable display order for slider banners (ascending: smallest = first slide / main background).
 * Uses the first recognized order field; falls back to `id` ascending; then original index.
 */
export function getBannerDisplayOrder(b: Banner): number {
  for (const key of BANNER_ORDER_FIELD_KEYS) {
    const n = numericFromUnknown(b[key]);
    if (n !== null) {
      return n;
    }
  }
  const id_n = numericFromUnknown(b.id);
  if (id_n !== null) {
    return id_n;
  }
  return Number.MAX_SAFE_INTEGER;
}

/**
 * Copy sorted by display order. Ties keep relative order (stable).
 * Use for main-screen background pick only; leave raw API arrays untouched for onboarding title-matching etc.
 */
export function sortBannersByDisplayOrder(banners: Banner[]): Banner[] {
  return [...banners]
    .map((banner, index) => ({ banner, index }))
    .sort((a, b) => {
      const diff = getBannerDisplayOrder(a.banner) - getBannerDisplayOrder(b.banner);
      if (diff !== 0) {
        return diff;
      }
      return a.index - b.index;
    })
    .map(({ banner }) => banner);
}

/** YouTube watch / short / embed / youtu.be → 11-char id. */
export function extractYoutubeVideoId(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  const patterns = [
    /youtu\.be\/([\w-]{11})(?:[?&#/]|$)/i,
    /youtube\.com\/(?:embed|shorts|live|v)\/([\w-]{11})(?:[?&#/]|$)/i,
    /youtube\.com\/watch\?(?:[^#]*&)?v=([\w-]{11})(?:[?&#]|$)/i,
    /youtube\.com\/watch\?v=([\w-]{11})(?:[?&#]|$)/i,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m?.[1]) {
      return m[1];
    }
  }
  return null;
}

function classifyMediaUri(uri: string, source: 'link' | 'attachment'): BannerMediaKind {
  const youtube_id = extractYoutubeVideoId(uri);
  if (youtube_id) {
    return 'youtube';
  }
  if (VIDEO_EXT.test(uri)) {
    return 'video';
  }
  if (IMAGE_EXT.test(uri)) {
    return 'image';
  }
  // No extension: attachment is historically a still; bare `url` tries as a stream then falls back.
  return source === 'attachment' ? 'image' : 'video';
}

export function getBannerAttachmentUrl(banner: Banner): string | null {
  return nonEmptyUrl(banner.attachment) ?? nonEmptyUrl(banner.image);
}

export function getBannerLinkUrl(banner: Banner): string | null {
  return nonEmptyUrl(banner.url);
}

/**
 * Prefer non-empty `url` over `attachment`/`image`. Attachment is kept as runtime fallback when
 * the primary media fails. Empty both → null (plain background).
 */
export function resolveBannerHeroMedia(banner: Banner): BannerHeroMedia | null {
  const link = getBannerLinkUrl(banner);
  const attachment = getBannerAttachmentUrl(banner);

  if (link) {
    const kind = classifyMediaUri(link, 'link');
    const youtube_id = kind === 'youtube' ? extractYoutubeVideoId(link) : null;
    let fallback_kind: BannerMediaKind | null = null;
    let fallback_uri: string | null = null;
    if (attachment && attachment !== link) {
      fallback_kind = classifyMediaUri(attachment, 'attachment');
      fallback_uri = attachment;
      // Avoid youtube-as-fallback of another youtube; attachment is almost always a file.
      if (fallback_kind === 'youtube') {
        fallback_kind = null;
        fallback_uri = null;
      }
    }
    return {
      kind,
      uri: link,
      youtube_id,
      fallback_kind,
      fallback_uri,
    };
  }

  if (attachment) {
    const kind = classifyMediaUri(attachment, 'attachment');
    return {
      kind,
      uri: attachment,
      youtube_id: kind === 'youtube' ? extractYoutubeVideoId(attachment) : null,
      fallback_kind: null,
      fallback_uri: null,
    };
  }

  return null;
}

export function bannerHasHeroMedia(banner: Banner): boolean {
  return resolveBannerHeroMedia(banner) != null;
}

/**
 * Still image URI for Image / frost masks. Prefer primary image; else image attachment fallback.
 * Not used for video/YouTube primaries without an image fallback.
 */
export function getBannerImageUrl(banner: Banner): string | null {
  const media = resolveBannerHeroMedia(banner);
  if (!media) {
    return null;
  }
  if (media.kind === 'image') {
    return media.uri;
  }
  if (media.fallback_kind === 'image' && media.fallback_uri) {
    return media.fallback_uri;
  }
  return null;
}

/**
 * Hero slide for main background + copy: prefer CMS order among banners that have playable/display media.
 */
export function pickMainHeroBanner(banners: Banner[]): Banner | null {
  if (!Array.isArray(banners) || banners.length === 0) {
    return null;
  }
  const sorted = sortBannersByDisplayOrder(banners);
  for (const b of sorted) {
    if (bannerHasHeroMedia(b)) {
      return b;
    }
  }
  for (const b of banners) {
    if (bannerHasHeroMedia(b)) {
      return b;
    }
  }
  return sorted[0] ?? banners[0] ?? null;
}

function extractBannerList(res: unknown): Banner[] {
  if (res == null) {
    return [];
  }
  const r = res as Record<string, unknown>;
  if (Array.isArray(r?.data)) {
    return r.data as Banner[];
  }
  if (Array.isArray(r?.banners)) {
    return r.banners as Banner[];
  }
  if (r?.data && typeof r.data === 'object' && !Array.isArray(r.data)) {
    const data = r.data as Record<string, unknown>;
    if (Array.isArray(data.banners)) {
      return data.banners as Banner[];
    }
    if (Array.isArray(data.data)) {
      return data.data as Banner[];
    }
  }
  if (Array.isArray(r)) {
    return r as Banner[];
  }
  return [];
}

export async function fetchBanners(): Promise<Banner[]> {
  try {
    const path = (endpoints as { slider?: string }).slider ?? 'banners';
    const res = await apiGet<unknown>(path);
    const list = extractBannerList(res);
    return list.filter(
      b =>
        b &&
        (bannerHasHeroMedia(b) || b.title || b.arabic_title),
    );
  } catch {
    return [];
  }
}
